import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const AuthContext = createContext(null);
const DEVICE_KEY = "wali_device_id";

export const ROLE_LABEL = {
  super_admin: "Super Admin", admin_kesantrian: "Admin Kesantrian", rois_amm: "Rois Amm", wakil: "Wakil",
  petugas_absensi: "Petugas Absensi",
  pengasuh: "Pengasuh / Pembimbing", bendahara: "Bendahara", kasir: "Kasir", wali: "Wali Santri",
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const onExpired = (e) => {
      setUser(null);
      toast.warning(e.detail || "Sesi berakhir, silakan masuk kembali");
    };
    window.addEventListener("session-expired", onExpired);
    return () => window.removeEventListener("session-expired", onExpired);
  }, []);

  const finish = (data) => {
    if (data?.session_token) localStorage.setItem("session_token", data.session_token);
    if (data?.device_id) localStorage.setItem(DEVICE_KEY, data.device_id);
    setUser(data);
    return data;
  };

  const staffLogin = async (username, password) => {
    const res = await api.post("/auth/staff/login", { username, password });
    if (res.data?.otp_required) return res.data;
    return finish(res.data);
  };
  const staffVerifyOtp = async (challenge_id, code) => finish((await api.post("/auth/staff/verify-otp", { challenge_id, code })).data);
  const staffGoogle = async (session_id) => {
    const res = await api.post("/auth/staff/google", { session_id });
    if (res.data?.otp_required) return res.data;
    return finish(res.data);
  };

  const waliStart = async (induk) => (await api.post("/auth/wali/start", { induk })).data;
  const waliVerify = async (challenge_id, code) =>
    finish((await api.post("/auth/wali/verify", { challenge_id, code, device_name: navigator.userAgent.slice(0, 60) })).data);
  const waliSetPin = async (pin) => api.post("/auth/wali/set-pin", { pin, device_id: localStorage.getItem(DEVICE_KEY) });
  const waliPinLogin = async (pin) => finish((await api.post("/auth/wali/pin-login", { device_id: localStorage.getItem(DEVICE_KEY), pin })).data);
  const getDevice = async () => {
    const id = localStorage.getItem(DEVICE_KEY);
    if (!id) return null;
    try { return (await api.get(`/auth/wali/device/${id}`)).data; } catch { localStorage.removeItem(DEVICE_KEY); return null; }
  };
  const forgetDevice = () => localStorage.removeItem(DEVICE_KEY);

  const hasPerm = (perm) => !!user && user.role !== "wali" && (user.perms?.includes("*") || user.perms?.includes(perm));

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("session_token");
    const isStaff = user && user.role !== "wali";
    setUser(null);
    window.location.href = isStaff ? "/pengurus" : "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, staffLogin, staffVerifyOtp, staffGoogle, waliStart, waliVerify, waliSetPin, waliPinLogin, getDevice, forgetDevice, hasPerm, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
